"use client";

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Contact } from "@/lib/types/database";
import { ResponsivePopover } from "@wtc/ui";
import { Badge } from "@/components/ui/badge";
import { Mail, Pencil } from "lucide-react";
import { useUpdateContact } from "@/features/contacts/hooks";
import { TooltipWithContent } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  type EmailAliasesEditable,
  normalizeAliases,
  getEmailLocalPart,
  EMAIL_REGEX,
} from "./email-aliases-utils";
import { EmailAliasesEditorPanel } from "./EmailAliasesEditorPanel";

interface EmailAliasesEditorProps<T extends EmailAliasesEditable = Contact> {
  contact: T;
  onUpdate?: (updatedContact: T) => void;
  isEditing?: boolean;
  onEditChange?: (isEditing: boolean) => void;
  showEditorDirectly?: boolean;
  readOnlyMode?: boolean;
}

export function EmailAliasesEditor<T extends EmailAliasesEditable = Contact>({
  contact,
  onUpdate,
  isEditing = false,
  onEditChange,
  showEditorDirectly = false,
  readOnlyMode = false,
}: EmailAliasesEditorProps<T>) {
  const [isOpen, setIsOpen] = useState(isEditing || showEditorDirectly);
  const [newAlias, setNewAlias] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [localAliases, setLocalAliases] = useState<string[]>(
    normalizeAliases(contact.email_aliases)
  );
  const updateContact = useUpdateContact();

  const aliases = useMemo(() => localAliases, [localAliases]);

  useEffect(() => {
    setLocalAliases(normalizeAliases(contact.email_aliases));
  }, [contact.id, contact.email_aliases]);

  const validateEmail = useCallback((email: string): boolean => {
    return EMAIL_REGEX.test(email);
  }, []);

  const handleAddAlias = useCallback(async () => {
    const trimmedAlias = newAlias.trim();

    if (!trimmedAlias) {
      setValidationError("Email cannot be empty");
      return;
    }

    if (!validateEmail(trimmedAlias)) {
      setValidationError("Invalid email format");
      return;
    }

    if (trimmedAlias.toLowerCase() === contact.email.toLowerCase()) {
      setValidationError("This is already the primary email");
      return;
    }

    if (aliases.some((a) => a.toLowerCase() === trimmedAlias.toLowerCase())) {
      setValidationError("This email is already in the list");
      return;
    }

    setIsAdding(true);
    setValidationError(null);

    try {
      const updatedAliases = [...aliases, trimmedAlias];
      setLocalAliases(updatedAliases);
      setNewAlias("");

      if (!readOnlyMode) {
        await updateContact.mutateAsync({
          id: contact.id,
          updates: { email_aliases: updatedAliases },
        });
        toast.success("Email alias added");
      } else {
        toast.success("Email alias added");
      }

      onUpdate?.({ ...contact, email_aliases: updatedAliases });
    } catch (error) {
      console.error("Error adding email alias:", error);
      setLocalAliases(aliases);
      toast.error("Failed to add email alias");
      setValidationError("Failed to add email alias");
    } finally {
      setIsAdding(false);
    }
  }, [newAlias, contact, aliases, validateEmail, updateContact, onUpdate, readOnlyMode]);

  const handleRemoveAlias = useCallback(
    async (aliasToRemove: string) => {
      try {
        const updatedAliases = aliases.filter((a) => a !== aliasToRemove);
        setLocalAliases(updatedAliases);

        if (!readOnlyMode) {
          await updateContact.mutateAsync({
            id: contact.id,
            updates: { email_aliases: updatedAliases },
          });
          toast.success("Email alias removed");
        } else {
          toast.success("Email alias removed");
        }

        onUpdate?.({ ...contact, email_aliases: updatedAliases });
      } catch (error) {
        console.error("Error removing email alias:", error);
        setLocalAliases(aliases);
        toast.error("Failed to remove email alias");
      }
    },
    [contact, aliases, updateContact, onUpdate, readOnlyMode]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void handleAddAlias();
      }
    },
    [handleAddAlias]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onEditChange?.(open);
    },
    [onEditChange]
  );

  const handleAliasChange = useCallback((val: string) => {
    setNewAlias(val);
    setValidationError(null);
  }, []);

  const panelProps = {
    showEditorDirectly,
    contactEmail: contact.email,
    aliases,
    newAlias,
    onAliasChange: handleAliasChange,
    validationError,
    isAdding,
    isPending: updateContact.isPending,
    handleAddAlias,
    handleRemoveAlias,
    handleKeyPress,
    handleOpenChange,
  };

  if (showEditorDirectly) {
    return (
      <div className="space-y-4">
        <EmailAliasesEditorPanel {...panelProps} />
      </div>
    );
  }

  return (
    <ResponsivePopover
      open={isOpen}
      onOpenChange={handleOpenChange}
      content={<EmailAliasesEditorPanel {...panelProps} />}
      popoverSide="bottom"
      align="start"
      className="w-96"
    >
      <div className="flex items-center gap-1 flex-wrap max-w-full overflow-hidden group relative">
        <TooltipWithContent content={contact.email}>
          <Badge
            variant="default"
            className="bg-blue-100 text-blue-800 text-xs py-0.5 px-2 truncate dark:bg-blue-900/40 dark:text-blue-200"
          >
            <Mail className="h-2.5 w-2.5 mr-0.5 flex-shrink-0" />
            <span className="truncate">{getEmailLocalPart(contact.email)}</span>
          </Badge>
        </TooltipWithContent>
        {aliases.length > 0 && (
          <>
            {aliases.slice(0, 1).map((alias) => (
              <TooltipWithContent key={alias} content={alias}>
                <Badge variant="outline" className="text-xs py-0.5 px-2 truncate max-w-[80px]">
                  {getEmailLocalPart(alias)}
                </Badge>
              </TooltipWithContent>
            ))}
            {aliases.length > 1 && (
              <Badge variant="secondary" className="text-xs py-0.5 px-2">
                +{aliases.length - 1}
              </Badge>
            )}
          </>
        )}
        <button type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
            onEditChange?.(true);
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 p-1 hover:bg-blue-50 rounded hover:shadow-sm text-gray-400 hover:text-blue-600 dark:hover:bg-blue-950/40"
          title="Click to edit"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
    </ResponsivePopover>
  );
}
